import React, { useContext, useEffect } from 'react'
import { AppSettings } from '../../config/app-settings'
import { toast } from 'react-toastify'
import Loader from '../pages/loader'
import { useParams } from 'react-router-dom'
import { Card, CardBody, CardHeader } from '../../components/other/card/card'
import { useGetIptvServerByIdQuery } from '../../redux/apis/linkManagement/iptv/server/lmIptvServerApiSlice'

const IptvPiracyInfo = React.memo(() => {
  const context = useContext(AppSettings)
  const { id } = useParams()
  const { data, isLoading, error } = useGetIptvServerByIdQuery({
    expand: [
      'last_updated_by',
      'status',
      'iptv',
      'ip_address',
      'server_type',
      'customers'
    ].join(','),
    id: id
  })

  useEffect(() => {
    if (error?.data) {
      const errorMessages = Object.values(error.data).flat()
      errorMessages.forEach(message => {
        toast.error(message)
      })
    }
  }, [error])

  useEffect(() => {
    context.setAppHeaderNone(false)
    context.setAppSidebarNone(false)
    context.setAppContentClass('')
    context.setAppThemePanal(true)

    return () => {
      context.setAppHeaderNone(true)
      context.setAppSidebarNone(true)
      context.setAppContentClass('p-0')
      context.setAppThemePanal(false)
    }
  }, [context]) // Add context as a dependency
  const record = data?.data

  return (
    <div className='w-full flex flex-col'>
      <Loader isLoading={isLoading} />

      {/* Server Info */}
      <Card>
        <CardHeader>{record?.iptv?.app_name}</CardHeader>
        <CardBody>
          <table className='table table-striped'>
            <tbody>
              <tr>
                <th scope='row'>Application Name</th>
                <td>{record?.iptv?.app_name}</td>
              </tr>
              <tr>
                <th scope='row'>Domain Name</th>
                <td>{record?.domain || '—'}</td>
              </tr>
              <tr>
                <th scope='row'>Server Type</th>
                <td>{record?.server_type?.server_type || '—'}</td>
              </tr>
              <tr>
                <th scope='row'>Server IP Address</th>
                <td>{record?.ip_address?.ip_address || '—'}</td>
              </tr>
              <tr>
                <th scope='row'>Status</th>
                <td>{record?.status?.status_name}</td>
              </tr>
              <tr>
                <th scope='row'>Takedown Channels</th>
                <td>{record?.takedown_channels ? 'Yes' : 'No'}</td>
              </tr>
              <tr>
                <th scope='row'>Takedown Campaigns</th>
                <td>{record?.takedown_campaigns ? 'Yes' : 'No'}</td>
              </tr>
              <tr>
                <th scope='row'>Created At</th>
                <td>{record?.created_at}</td>
              </tr>
              <tr>
                <th scope='row'>Updated At</th>
                <td>{record?.updated_at}</td>
              </tr>
              <tr>
                <th scope='row'>Customers</th>
                <td>
                  {record?.customers?.length > 0
                    ? record.customers.map(c => c.customer_name).join(', ')
                    : '—'}
                </td>
              </tr>
              <tr>
                <th scope='row'>Last Updated By</th>
                <td>
                  {record?.last_updated_by?.first_name}{' '}
                  {record?.last_updated_by?.last_name}
                </td>
              </tr>
            </tbody>
          </table>
        </CardBody>
      </Card>
      <br />

      {/* IPTV Subscription */}
      {record?.iptv && (
        <>
          <Card>
            <CardHeader>IPTV Subscription</CardHeader>
            <CardBody>
              <table className='table table-striped'>
                <tbody>
                  <tr>
                    <th scope='row'>Developer</th>
                    <td>{record.iptv.developer || '—'}</td>
                  </tr>
                  <tr>
                    <th scope='row'>Version</th>
                    <td>{record.iptv.version || '—'}</td>
                  </tr>
                  <tr>
                    <th scope='row'>Username</th>
                    <td>{record.iptv.subscription_username || '—'}</td>
                  </tr>
                  <tr>
                    <th scope='row'>Password</th>
                    <td>{record.iptv.subscription_password || '—'}</td>
                  </tr>
                  <tr>
                    <th scope='row'>Code</th>
                    <td>{record.iptv.subscription_code || '—'}</td>
                  </tr>
                  <tr>
                    <th scope='row'>Expiry</th>
                    <td>{record.iptv.subscription_expiry || '—'}</td>
                  </tr>
                  {record.iptv.extra_info && (
                    <tr>
                      <th scope='row'>Extra Info</th>
                      <td>{record.iptv.extra_info}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardBody>
          </Card>
          <br />
        </>
      )}

      {/* Campaigns */}
      {record?.campaigns?.length > 0 && (
        <>
          <Card>
            <CardHeader>Campaigns</CardHeader>
            <CardBody>
              <table className='table table-striped'>
                <thead>
                  <tr>
                    <th>Campaign Name</th>
                    <th>URLs</th>
                  </tr>
                </thead>
                <tbody>
                  {record.campaigns.map(campaign => (
                    <tr key={campaign.campaign_id}>
                      <td>{campaign.campaign_name}</td>
                      <td>
                        {campaign.urls?.map((url, i) => (
                          <div key={i}>
                            <a href={url} target='_blank' rel='noreferrer'>{url}</a>
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardBody>
          </Card>
          <br />
        </>
      )}

      {/* Channels */}
      {record?.channels?.length > 0 && (
        <>
          <Card>
            <CardHeader>Channels</CardHeader>
            <CardBody>
              <table className='table table-striped'>
                <thead>
                  <tr>
                    <th>Channel Name</th>
                    <th>URL</th>
                  </tr>
                </thead>
                <tbody>
                  {record.channels.map((channel, i) => (
                    <tr key={channel.channel_id ?? i}>
                      <td>{channel.channel_name || '—'}</td>
                      <td>
                        {channel.url ? (
                          <a href={channel.url} target='_blank' rel='noreferrer'>
                            {channel.url}
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardBody>
          </Card>
          <br />
        </>
      )}
      {/* Extra Information */}
      <Card>
        <CardHeader>Extra Information</CardHeader>
        <CardBody>
          {record?.extra_info?.split('\n')?.map((x, i) => (
            <p key={i}>{x}</p>
          ))}
        </CardBody>
      </Card>
    </div>
  )
})

export default IptvPiracyInfo
